import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Mic,
  Square,
  Play,
  Pause,
  RotateCcw,
  Send,
  CheckCircle2,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useT } from '../i18n';
import { useKiooAudio } from '../AudioProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 60;

// Feature detection for MediaRecorder + best available mime type
const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/mpeg',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return '';
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const MobileMessage = () => {
  const t = useT();
  const { currentStation } = useKiooAudio();

  const [phase, setPhase] = useState('idle'); // idle | recording | review | submitting | sent | error
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [error, setError] = useState('');
  const [previewPlaying, setPreviewPlaying] = useState(false);

  const [form, setForm] = useState({
    listener_name: '',
    location: '',
    message_for: '',
    phone: '',
    consent: false,
  });

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startRef = useRef(0);
  const tickRef = useRef(null);
  const previewAudioRef = useRef(null);

  const supported = typeof MediaRecorder !== 'undefined';

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  useEffect(() => () => cleanupStream(), []);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const startRecording = async () => {
    setError('');
    if (!supported) {
      setError(
        'Your browser does not support voice recording. Please try Chrome (Android) or Safari 14+ (iPhone).'
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const mt = pickMime();
      const rec = new MediaRecorder(
        stream,
        mt ? { mimeType: mt, audioBitsPerSecond: 64_000 } : undefined
      );
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const actualMime = mt || rec.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type: actualMime });
        setBlob(b);
        setMimeType(actualMime);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(URL.createObjectURL(b));
        setPhase('review');
        cleanupStream();
      };
      mediaRecorderRef.current = rec;
      startRef.current = Date.now();
      setElapsed(0);
      setPhase('recording');
      rec.start(250);
      tickRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startRef.current) / 1000);
        setElapsed(s);
        if (s >= MAX_SECONDS) stopRecording();
      }, 250);
    } catch (e) {
      setError(
        'Microphone permission was denied. Please allow microphone access and try again.'
      );
      cleanupStream();
    }
  };

  const stopRecording = () => {
    try {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
    } catch (_e) {
      /* ignore */
    }
  };

  const togglePreview = () => {
    const el = previewAudioRef.current;
    if (!el) return;
    if (previewPlaying) {
      el.pause();
      setPreviewPlaying(false);
    } else {
      el.play();
      setPreviewPlaying(true);
    }
  };

  const recordAgain = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setMimeType(null);
    setElapsed(0);
    setPhase('idle');
    setPreviewPlaying(false);
  };

  const submit = async () => {
    setError('');
    if (!form.consent) {
      setError('Please tick the consent box to continue.');
      return;
    }
    if (!form.listener_name.trim() || !form.location.trim() || !form.message_for.trim()) {
      setError('Please fill in Name, Location, and Who is this message for.');
      return;
    }
    if (!blob) {
      setError('No recording found. Please record again.');
      return;
    }
    setPhase('submitting');
    const fd = new FormData();
    const ext =
      (mimeType || 'audio/webm').includes('mp4') ? 'm4a'
      : (mimeType || 'audio/webm').includes('ogg') ? 'ogg'
      : (mimeType || 'audio/webm').includes('mpeg') ? 'mp3'
      : 'webm';
    fd.append('audio', blob, `recording.${ext}`);
    fd.append('listener_name', form.listener_name.trim());
    fd.append('location', form.location.trim());
    fd.append('message_for', form.message_for.trim());
    if (form.phone.trim()) fd.append('phone', form.phone.trim());
    fd.append('duration_seconds', String(elapsed || 1));
    fd.append('consent', 'true');
    fd.append('station_slug', currentStation?.slug || 'kioo');
    try {
      await axios.post(`${BACKEND_URL}/api/voice-messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhase('sent');
    } catch (e) {
      const msg =
        e?.response?.data?.detail || 'Could not send your message. Please try again.';
      setError(String(msg));
      setPhase('review');
    }
  };

  // ------------------ RENDER ---------------------------------------------

  const stationName = currentStation?.name || 'Kioo Radio';

  if (phase === 'sent') {
    return (
      <div className="px-4 pt-8 pb-4">
        <div
          data-testid="mobile-message-sent"
          className="rounded-2xl bg-white border p-5 text-center shadow-sm"
          style={{ borderColor: 'var(--kioo-primary)' }}
        >
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ background: 'var(--kioo-background)' }}
          >
            <CheckCircle2
              className="w-10 h-10"
              style={{ color: 'var(--kioo-primary)' }}
            />
          </div>
          <h2
            className="mt-4 text-xl font-extrabold"
            style={{ color: 'var(--kioo-primary-dark)' }}
          >
            Thank you!
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Your voice message has been sent to {stationName}. Our team will
            review it before it goes on air.
          </p>
          <button
            data-testid="mobile-message-send-another"
            onClick={recordAgain}
            className="mt-5 w-full py-3 rounded-full font-semibold text-white"
            style={{ background: 'var(--kioo-primary)' }}
          >
            Record another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-6">
      {/* Station-branded banner with a big transparent watermark logo. */}
      <div
        data-testid="mobile-message-banner"
        className="relative overflow-hidden rounded-2xl px-4 py-4 text-white shadow-md"
        style={{
          background:
            'linear-gradient(135deg, var(--kioo-primary-dark) 0%, var(--kioo-primary) 55%, var(--kioo-accent) 100%)',
        }}
      >
        {currentStation?.logo_url && (
          <img
            src={currentStation.logo_url}
            alt=""
            aria-hidden="true"
            data-testid="mobile-message-banner-bg-logo"
            className="pointer-events-none select-none absolute inset-0 m-auto h-[130%] w-auto object-contain opacity-20"
            style={{ mixBlendMode: 'screen' }}
          />
        )}
        <div className="relative z-10">
          <h1
            className="text-2xl font-extrabold leading-tight"
            data-testid="mobile-message-title"
          >
            Send a Message to {stationName}
          </h1>
          <p className="text-xs text-white/90 mt-1">
            Record up to {MAX_SECONDS} seconds. We will review before broadcasting.
          </p>
          {currentStation?.logo_url && (
            <div className="mt-2 inline-flex items-center gap-2 bg-white/15 rounded-full pr-3 pl-1 py-1">
              <img
                src={currentStation.logo_url}
                alt={stationName}
                className="h-7 w-auto object-contain"
                style={{ background: 'transparent' }}
                data-testid="mobile-message-station-logo"
              />
              <span
                className="text-[11px] font-extrabold uppercase tracking-wide"
                data-testid="mobile-message-station-name"
              >
                {stationName}
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          data-testid="mobile-message-error"
          className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Recorder card */}
      <div
        data-testid="mobile-message-recorder"
        className="mt-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm text-center"
      >
        <div
          data-testid="mobile-message-timer"
          className="text-3xl font-extrabold tabular-nums"
          style={{
            color:
              phase === 'recording'
                ? '#dc2626'
                : 'var(--kioo-primary-dark)',
          }}
        >
          {formatTime(elapsed)}{' '}
          <span className="text-gray-400 text-xl">/ {formatTime(MAX_SECONDS)}</span>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${(elapsed / MAX_SECONDS) * 100}%`,
              background:
                phase === 'recording' ? '#ef4444' : 'var(--kioo-primary)',
            }}
          />
        </div>

        <div className="mt-6 flex items-center justify-center">
          {phase === 'idle' && (
            <button
              data-testid="mobile-message-record-btn"
              onClick={startRecording}
              className="w-24 h-24 rounded-full text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
              }}
              aria-label="Start recording"
            >
              <Mic className="w-10 h-10" />
            </button>
          )}
          {phase === 'recording' && (
            <button
              data-testid="mobile-message-stop-btn"
              onClick={stopRecording}
              className="w-24 h-24 rounded-full bg-gray-800 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform animate-pulse"
              aria-label="Stop recording"
            >
              <Square className="w-10 h-10 fill-current" />
            </button>
          )}
          {phase === 'review' && (
            <button
              data-testid="mobile-message-preview-btn"
              onClick={togglePreview}
              className="w-24 h-24 rounded-full text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              style={{ background: 'var(--kioo-primary)' }}
              aria-label={previewPlaying ? 'Pause preview' : 'Play preview'}
            >
              {previewPlaying ? (
                <Pause className="w-10 h-10" />
              ) : (
                <Play className="w-10 h-10 fill-current" />
              )}
            </button>
          )}
        </div>

        <div className="mt-2 text-sm font-medium text-gray-600">
          {phase === 'idle' && 'Tap the mic to start'}
          {phase === 'recording' && 'Recording…'}
          {phase === 'review' && 'Tap to preview your recording'}
        </div>

        {phase === 'review' && (
          <div className="mt-4 flex gap-2">
            <button
              data-testid="mobile-message-redo-btn"
              onClick={recordAgain}
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-sm"
            >
              <RotateCcw className="w-4 h-4" />
              Record again
            </button>
          </div>
        )}

        {blobUrl && (
          <audio
            ref={previewAudioRef}
            src={blobUrl}
            onEnded={() => setPreviewPlaying(false)}
            className="hidden"
            data-testid="mobile-message-preview-audio"
          />
        )}
      </div>

      {/* Form (shown after recording is captured) */}
      {phase === 'review' && (
        <div
          data-testid="mobile-message-form"
          className="mt-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm space-y-3"
        >
          <FormField
            label="Your Name *"
            testid="mobile-message-name"
            value={form.listener_name}
            onChange={(v) => setForm({ ...form, listener_name: v })}
          />
          <FormField
            label="Your Location / Town *"
            testid="mobile-message-location"
            value={form.location}
            onChange={(v) => setForm({ ...form, location: v })}
          />
          <FormField
            label="Who is this message for? *"
            testid="mobile-message-recipient"
            value={form.message_for}
            onChange={(v) => setForm({ ...form, message_for: v })}
          />
          <FormField
            label="Phone / WhatsApp (optional)"
            testid="mobile-message-phone"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            type="tel"
          />
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              data-testid="mobile-message-consent"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 accent-emerald-600 w-4 h-4"
            />
            <span>
              I understand that Kioo Radio may review, edit, and broadcast this
              recording.
            </span>
          </label>

          <button
            data-testid="mobile-message-submit-btn"
            onClick={submit}
            disabled={phase === 'submitting' || !form.consent}
            className="w-full py-3 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: 'var(--kioo-primary)' }}
          >
            {phase === 'submitting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send to {stationName}
              </>
            )}
          </button>
        </div>
      )}

      {!supported && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          Recording isn't supported in this browser. Please open the app in
          Chrome (Android) or Safari 14+ (iPhone).
        </div>
      )}
    </div>
  );
};

const FormField = ({ label, value, onChange, testid, type = 'text' }) => (
  <label className="block">
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
    />
  </label>
);

export default MobileMessage;
